import React, { useState } from 'react';
import { CATEGORIAS_CIRCULARIZACAO, CategoriaCircularizacao, IRegistroCircularizacao } from '../interfaces/ICircularizacao';
import { JUSTIFICATIVA_PADRAO_EDICAO } from '../utils/justificativasPadrao';

interface Props {
  registro: IRegistroCircularizacao;
  salvando: boolean;
  onCancelar: () => void;
  onConfirmar: (dados: {
    razaoSocialExibicao: string;
    nomeFantasia?: string;
    cnpj: string;
    categoria: CategoriaCircularizacao;
  }, justificativa: string) => void;
}

// Seção 4.3 — "Editar Empresa". A alteração aqui só afeta os dados de trabalho da circularização,
// nunca o cadastro mestre de empresas da AI Númera (seção 15). A troca de Categoria exige
// confirmação explícita quando a carta já estiver preparada ("mediante confirmação quando houver
// documentos preparados").
const EditarEmpresaModal: React.FC<Props> = ({ registro, salvando, onCancelar, onConfirmar }) => {
  const [razaoSocial, setRazaoSocial] = useState(registro.razaoSocialExibicao);
  const [nomeFantasia, setNomeFantasia] = useState(registro.nomeFantasia ?? '');
  const [cnpj, setCnpj] = useState(registro.cnpj);
  const [categoria, setCategoria] = useState<CategoriaCircularizacao>(registro.categoria);
  const [justificativa, setJustificativa] = useState(JUSTIFICATIVA_PADRAO_EDICAO);
  const [tentouEnviar, setTentouEnviar] = useState(false);
  const [confirmouTrocaCategoria, setConfirmouTrocaCategoria] = useState(false);

  const categoriaAlterada = categoria !== registro.categoria;
  const cartaJaPreparada = registro.situacaoPreparacaoCarta === 'PRONTA';
  const houveAlteracao =
    razaoSocial.trim() !== registro.razaoSocialExibicao ||
    (nomeFantasia.trim() || undefined) !== registro.nomeFantasia ||
    cnpj.trim() !== registro.cnpj ||
    categoriaAlterada;

  const valido =
    razaoSocial.trim().length > 0 &&
    cnpj.trim().length > 0 &&
    (!houveAlteracao || justificativa.trim().length > 0) &&
    (!categoriaAlterada || !cartaJaPreparada || confirmouTrocaCategoria);

  const confirmar = () => {
    setTentouEnviar(true);
    if (!valido) return;
    onConfirmar(
      { razaoSocialExibicao: razaoSocial.trim(), nomeFantasia: nomeFantasia.trim() || undefined, cnpj: cnpj.trim(), categoria },
      justificativa.trim()
    );
  };

  return (
    <div className='circ-modal-backdrop' onMouseDown={(e) => e.target === e.currentTarget && onCancelar()}>
      <div className='circ-modal'>
        <div className='circ-modal-header'>
          <h4>Editar Empresa</h4>
          <button type='button' className='btn btn-sm btn-icon btn-light' aria-label='Fechar' onClick={onCancelar}>
            <i className='bi bi-x-lg' />
          </button>
        </div>
        <div className='circ-modal-body'>
          <div className='circ-form-group'>
            <label htmlFor='circ-editar-categoria'>Categoria</label>
            <select
              id='circ-editar-categoria'
              className='circ-form-control'
              value={categoria}
              onChange={(e) => {
                setCategoria(e.target.value as CategoriaCircularizacao);
                setConfirmouTrocaCategoria(false);
              }}
            >
              {CATEGORIAS_CIRCULARIZACAO.map((c) => (
                <option key={c.value} value={c.value}>
                  {c.label}
                </option>
              ))}
            </select>
            {categoriaAlterada && cartaJaPreparada && (
              <div className='circ-form-group mt-2' style={{ background: '#fff8e8', border: '1px solid #ffe29a', borderRadius: 8, padding: '8px 12px' }}>
                <div className='form-check'>
                  <input
                    id='circ-confirmar-troca-categoria'
                    type='checkbox'
                    className='form-check-input'
                    checked={confirmouTrocaCategoria}
                    onChange={(e) => setConfirmouTrocaCategoria(e.target.checked)}
                  />
                  <label htmlFor='circ-confirmar-troca-categoria' className='form-check-label' style={{ fontSize: 12.5, color: '#8a5a00' }}>
                    A carta desta empresa já foi preparada para a categoria atual. Confirmo a troca de categoria
                    mesmo assim (a carta deverá ser revisada e preparada novamente).
                  </label>
                </div>
              </div>
            )}
          </div>
          <div className='circ-form-group'>
            <label className='circ-campo-obrigatorio'>Razão Social / Nome de Exibição</label>
            <input className='circ-form-control' value={razaoSocial} onChange={(e) => setRazaoSocial(e.target.value)} />
          </div>
          <div className='circ-form-group'>
            <label>Nome Fantasia</label>
            <input className='circ-form-control' value={nomeFantasia} onChange={(e) => setNomeFantasia(e.target.value)} />
          </div>
          <div className='circ-form-group'>
            <label className='circ-campo-obrigatorio'>CPF/CNPJ</label>
            <input className='circ-form-control' value={cnpj} onChange={(e) => setCnpj(e.target.value)} />
          </div>

          {houveAlteracao && (
            <div className='circ-form-group'>
              <label className='circ-campo-obrigatorio'>Justificativa da Alteração</label>
              <textarea
                className='circ-form-control'
                rows={3}
                value={justificativa}
                onChange={(e) => setJustificativa(e.target.value)}
                placeholder='Ex.: Correção da razão social para identificação adequada do destinatário da confirmação externa.'
              />
              {tentouEnviar && justificativa.trim().length === 0 && (
                <div className='text-danger mt-1' style={{ fontSize: 12 }}>
                  A justificativa da alteração é obrigatória quando algum dado é modificado.
                </div>
              )}
              <div className='mt-2' style={{ fontSize: 12, color: '#6b7280' }}>
                <strong>Nome Original:</strong> {registro.razaoSocialOriginal}
              </div>
            </div>
          )}
        </div>
        <div className='circ-modal-footer'>
          <button type='button' className='btn btn-sm btn-light' disabled={salvando} onClick={onCancelar}>
            Cancelar
          </button>
          <button type='button' className='btn btn-sm btn-primary' disabled={salvando || !houveAlteracao} onClick={confirmar}>
            {salvando && <span className='spinner-border spinner-border-sm me-1' />}
            Salvar Alterações
          </button>
        </div>
      </div>
    </div>
  );
};

export default EditarEmpresaModal;
